import Group from 'components/utils/Group'
import Modal from 'LuxKit/Modal/Modal'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import FormatCurrency from 'components/Common/FormatCurrency'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import { rem } from 'polished'
import LineCopyIcon from 'LuxKit/Icons/line/LineCopyIcon'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import useCopyWithFeedback from 'luxPlus/hooks/useCopyWithFeedback'
import { LUXURY_PLUS, LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import HelloFreshModalImage from './HelloFreshModalImage'

const PromoClickableListItem = styled(ClickableListItem)`
  background-color: ${props => props.theme.palette.neutral.default.six};
  max-width: ${rem(336)};
`
interface Props {
  benefit?: App.MemberSubscriptionBenefitItem
}

function HelloFreshCampaignModal({ benefit }: Props) {
  const intl = useIntl()
  const code = benefit?.code ?? (benefit ? 'HELLOLUXPLUS' : undefined)
  const { isCopied, handleCopy } = useCopyWithFeedback(code ?? '')

  const titleText = <FormattedMessage
    id="3pl-1okhP7IbwgkUgfbx"
    defaultMessage="Enjoy up to {amount} off 10 {brandName} boxes"
    description="Title for HelloFresh partner campaign modal showing discount amount"
    values={{
      amount: <FormatCurrency value={419} format="rounded" />,
      brandName: 'HelloFresh',
    }}
  />

  const saveButtonText = intl.formatMessage(
    {
      id: 'EqhcPbobGkE7aDKNK5LQ',
      defaultMessage: 'Save on {brandName} Now!',
      description: 'Button text to save on HelloFresh from partner campaign modal',
    },
    { brandName: 'HelloFresh' },
  )

  const disclaimerText = intl.formatMessage({
    id: 'IeBKIapnWbiYzp-o5v-y',
    defaultMessage: 'This voucher entitles you to up to: 40% off your first box, 30% off your second box, 20% off your third box, and 10% off your next seven boxes, subject to the voucher being utilised within 100 days of your subscription. Offer only valid for new customers and past customers (cancelled for over 6 months). Discount is non-transferable, non-exchangeable, excludes upgrades, add-ons and is invalid in conjunction with any other offer. By using this voucher, you will be signed up to an ongoing weekly subscription.',
    description: 'Voucher terms disclaimer for HelloFresh partner campaign offer',
  })

  const termsLinkText = intl.formatMessage({
    id: '6lDWsnt7y9nSMJ4i78zM',
    defaultMessage: 'Terms and conditions apply.',
    description: 'Terms and conditions link text for HelloFresh partner campaign',
  })

  return (
    <Modal
      size="L"
      title={titleText}
      primaryActionText={benefit ? saveButtonText : undefined}
      primaryActionHref={`https://www.hellofresh.com.au/plans?discount_comm_id=7949a168-06c2-4a5c-9c7f-e739dc385420&c=${code}`}
      primaryActionTarget="_blank"
    >
      <Group direction="vertical" gap={20}>
        <HelloFreshModalImage />
        <div>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'khsLan8aMuQsalhkMSGm',
              defaultMessage: 'Elevate your everyday dining with HelloFresh, where fresh ingredients, chef-designed recipes and effortless convenience meet premium flavour.',
              description: 'Introductory text describing the HelloFresh meal delivery service and its benefits',
            })}
            <br />
            <br />
            <FormattedMessage
              id="tWoMqX5-mt62fpm-Av57"
              defaultMessage="From luxury travel to luxury meals at home, your {programName} membership unlocks an exclusive offer: save up to {savingsAmount} on your next 10 boxes. Discover delicious, beautifully balanced meals delivered to your door, ready to enjoy whenever you are."
              description="HelloFresh campaign offer details showing savings amount for LuxPlus+ members"
              values={{
                programName: LUXURY_PLUS.PROGRAM_NAME,
                savingsAmount: <FormatCurrency value={419} format="rounded" />,
              }}
            />
            <br />
            <br />
            {intl.formatMessage({
              id: 'LIzZQ678y6SW5BDLmDHj',
              defaultMessage: 'Indulge in a little everyday luxury, made simple.',
              description: 'Tagline encouraging users to enjoy HelloFresh as part of their everyday luxury experience',
            })}
            <br />
            <br />
            {intl.formatMessage({
              id: 'MsNJs9mQXsKFQAnBGR0u',
              defaultMessage: 'Click ‘Save on HelloFresh Now!’ below and your exclusive discount code will be applied automatically.',
              description: 'Instructions telling users to click the save button to apply their HelloFresh discount code',
            })}
            <br />
            <br />
          </BodyText>
        </div>
        {code && <PromoClickableListItem
          title={<b>{code}</b>}
          endIcon={isCopied ? <LineCheckIcon size="S" /> : <LineCopyIcon size="S" />}
          onClick={handleCopy}
          selected={isCopied}
        />}

        <BodyText variant="small">
          {disclaimerText}
          <br />
          <TextLink
            target="_blank"
            to={`/${LUXURY_PLUS_PATHS.TERMS_AND_CONDITIONS_PAGE}`}
            weight="regular"
          >
            {termsLinkText}
          </TextLink>
        </BodyText>
      </Group>
    </Modal>
  )
}

export default HelloFreshCampaignModal
